<template>
  <ExecuteLockGuard>
    <FloatingMenu v-tooltip="reviewItem.fullTextScreening">
      <template #activator="{ show }">
        <StudyActionButton
          :is-loading="isLoading"
          :state="
            reviewItem.fullTextScreening === FULL_TEXT_CANNOT_BE_RETRIEVED_CRITERION_TYPE
              ? ButtonState.fullTextCannotBeRetrieved
              : (reviewItem.titleAndAbstractScreening !== 'Included' &&
                    (review.entity.value.plan?.screeningPlan.titleAndAbstractCriteria.length ?? 0) >
                      0) ||
                  reviewItem.isDuplicate
                ? ButtonState.disabled
                : reviewItem.fullTextScreening === '' &&
                    !reviewItem.isDuplicate &&
                    reviewItem.suggestedFullTextScreening
                  ? isAiSuggestedInclusion
                    ? ButtonState.aiSuggestedInclusion
                    : ButtonState.aiSuggestedExclusion
                  : reviewItem.fullTextScreening === '' && !reviewItem.isDuplicate
                    ? ButtonState.inactive
                    : reviewItem.fullTextScreening === 'Included'
                      ? ButtonState.included
                      : ButtonState.excluded
          "
          :is-review-read-only="review.isReviewReadonly.value"
          @click.stop="show"
        >
          <span>{{
            review.entity.value.plan?.screeningPlan.titleAndAbstractCriteria.length === 0
              ? "Screening"
              : "Full text"
          }}</span>
        </StudyActionButton>
      </template>
      <template #content>
        <div class="flex flex-col items-stretch w-[400px] max-h-100" @click.stop.prevent>
          <button
            v-tooltip.right.redBackground="
              !reviewItem.pdfFile
                ? 'You must upload the full-text PDF to be able to screen on full text. <br> Drag/drop the PDF on the record card to upload it.'
                : ''
            "
            class="relative flex flex-col items-start px-6 py-3 text-left transition-all duration-200 ease-in-out rounded-md"
            :class="[
              {
                'hover:text-green-700 hover:bg-green-50': reviewItem.pdfFile,
                'bg-gray-100 text-gray-400 cursor-auto': !reviewItem.pdfFile,
                'brightness-95 text-green-700 bg-green-100':
                  reviewItem.fullTextScreening === INCLUDED,
                'border-2 bg-green-50 shadow-[0_0_15px_rgba(168,85,247,0.4)]':
                  !reviewItem.fullTextScreening && isAiSuggestedInclusion,
              },
              !reviewItem.fullTextScreening && isAiSuggestedInclusion ? 'border-purple-400' : '',
            ]"
            @click.stop="toggleIncluded"
          >
            <span class="font-medium">Included</span>
            <span
              v-if="!reviewItem.fullTextScreening && isAiSuggestedInclusion"
              class="relative mt-1 text-sm tracking-wide uppercase text-purple-600 font-semibold overflow-hidden"
            >
              <span class="relative z-10 animate-pulse"> AI suggestion</span>
            </span>
          </button>

          <button
            class="px-6 py-3 text-left"
            :class="{
              'bg-gray-100 text-gray-400 cursor-auto': reviewItem.pdfFile,
              'text-blue-700 hover:brightness-95': !reviewItem.pdfFile,
              'brightness-95':
                reviewItem.fullTextScreening === FULL_TEXT_CANNOT_BE_RETRIEVED_CRITERION_TYPE,
            }"
            @click.stop="toggleFullTextCannotBeRetrieved"
          >
            Full text cannot be retrieved
          </button>
          <template
            v-for="criterion in review.entity.value?.plan?.screeningPlan?.fullTextCriteria?.filter(
              (c) => c !== FULL_TEXT_CANNOT_BE_RETRIEVED_CRITERION_TYPE,
            )"
            :key="criterion"
          >
            <button
              v-tooltip.right="
                !reviewItem.pdfFile &&
                (review.entity.value.plan?.screeningPlan?.titleAndAbstractCriteria.length ?? 0) > 0
                  ? 'You must upload the full-text PDF to be able to screen on full text. <br> Drag/drop the PDF on the record card to upload it.'
                  : ''
              "
              class="relative flex flex-col items-start px-6 py-3 text-left transition-all duration-200 ease-in-out rounded-md"
              :class="[
                !!props.reviewItem.pdfFile ||
                review.entity.value.plan?.screeningPlan.titleAndAbstractCriteria.length === 0
                  ? 'hover:bg-red-50 text-red-700'
                  : 'bg-gray-200 text-gray-500 cursor-auto',
                {
                  'brightness-95 bg-red-50': reviewItem.fullTextScreening === criterion,
                  'border-2 bg-red-50 shadow-[0_0_15px_rgba(168,85,247,0.4)] border-purple-400':
                    !reviewItem.fullTextScreening &&
                    reviewItem.suggestedFullTextScreening === criterion,
                },
              ]"
              @click.stop="toggleExcluded(criterion)"
            >
              <span class="font-medium">{{ criterion }}</span>
              <span
                v-if="
                  !reviewItem.fullTextScreening &&
                  reviewItem.suggestedFullTextScreening === criterion
                "
                class="relative mt-1 text-sm tracking-wide uppercase text-purple-600 font-semibold overflow-hidden"
              >
                <span class="relative z-10 animate-pulse"> AI suggestion</span>
              </span>
            </button>
          </template>
        </div>
      </template>
    </FloatingMenu>
  </ExecuteLockGuard>
</template>
<script lang="ts" setup>
import FloatingMenu from "@app/components/Global/FloatingMenu.vue";
import { ButtonState } from "@app/types";
import { ReviewKey } from "@app/views/Review/use-review";
import { ReviewItem } from "@core/domain/models/reviewItem.model";
import { FULL_TEXT_CANNOT_BE_RETRIEVED_CRITERION_TYPE } from "@core/domain/types/full-text-cannot-be-retrieved-screening-criterion.type";
import { INCLUDED } from "@core/domain/types/included-screening-criterion.type";
import StudyActionButton from "../../StudyActionButton.vue";
import ExecuteLockGuard from "../../ExecuteLockGuard.vue";
import { injectStrict } from "@app/utils/injectStrict";
import { computed } from "vue";

const review = injectStrict(ReviewKey);
const props = defineProps<{
  reviewItem: ReviewItem;
  isLoading?: boolean;
}>();

const isAiSuggestedInclusion = computed(() => {
  return props.reviewItem.suggestedFullTextScreening === "Included";
});

const emit = defineEmits<{
  (e: "clearFullTextScreening"): void;
  (e: "setFullTextScreening", value: string): void;
}>();

function toggleIncluded() {
  if (props.reviewItem.fullTextScreening === INCLUDED) emit("clearFullTextScreening");
  else if (props.reviewItem.pdfFile) emit("setFullTextScreening", INCLUDED);
}

function toggleFullTextCannotBeRetrieved() {
  if (props.reviewItem.fullTextScreening === FULL_TEXT_CANNOT_BE_RETRIEVED_CRITERION_TYPE)
    emit("clearFullTextScreening");
  else if (!props.reviewItem.pdfFile)
    emit("setFullTextScreening", FULL_TEXT_CANNOT_BE_RETRIEVED_CRITERION_TYPE);
}

function toggleExcluded(criterion: string) {
  if (props.reviewItem.fullTextScreening === criterion) emit("clearFullTextScreening");
  else if (
    !!props.reviewItem.pdfFile ||
    review.entity.value.plan?.screeningPlan.titleAndAbstractCriteria.length === 0
  )
    emit("setFullTextScreening", criterion);
}
</script>
